<template>
  <div class="journey-col" v-if="progress">
    <div class="tooltip-wrapper">
      <div
        class="journey-dots"
        :class="{ 'is-collapsible': isCollapsible }"
        :style="isCancelled ? 'opacity: 0.55' : ''"
      >
        <!-- COLLAPSIBLE: two layers that swap on hover -->
        <template v-if="isCollapsible">
          <!-- Preview: 3 smart dots + badge -->
          <div class="j-layer j-preview">
            <template v-for="(step, idx) in previewSteps">
              <div
                :key="'pd-' + idx"
                class="j-dot"
                :class="dotClass(step.status)"
              ></div>
              <div
                v-if="idx < previewSteps.length - 1"
                :key="'pc-' + idx"
                class="j-connector"
                :class="connectorClass(step.status, previewSteps[idx + 1].status)"
              ></div>
            </template>
            <div class="j-badge">{{ progress.completed_steps }}/{{ progress.total_steps }}</div>
          </div>
          <!-- Full: all dots (shown on hover) -->
          <div class="j-layer j-full">
            <template v-for="(step, idx) in progress.steps">
              <div
                :key="'fd-' + idx"
                class="j-dot"
                :class="dotClass(step.status)"
              ></div>
              <div
                v-if="idx < progress.steps.length - 1"
                :key="'fc-' + idx"
                class="j-connector"
                :class="isStepDone(step.status) ? 'done' : 'pending'"
              ></div>
            </template>
          </div>
        </template>

        <!-- NOT COLLAPSIBLE: render all dots directly -->
        <template v-else>
          <template v-for="(step, idx) in progress.steps">
            <div
              :key="'dot-' + idx"
              class="j-dot"
              :class="dotClass(step.status)"
            ></div>
            <div
              v-if="idx < progress.steps.length - 1"
              :key="'conn-' + idx"
              class="j-connector"
              :class="isStepDone(step.status) ? 'done' : 'pending'"
            ></div>
          </template>
          <div class="j-badge">{{ progress.completed_steps }}/{{ progress.total_steps }}</div>
        </template>
      </div>

      <!-- Tooltip -->
      <div class="tooltip-content">
        <div class="tooltip-title">{{ progress.journey_name }}</div>
        <div class="tooltip-steps">
          <div
            v-for="(step, idx) in progress.steps"
            :key="'tl-' + idx"
            class="tooltip-step"
            :class="{ 'done-line': step.status === 'completed' && isNextDone(idx) }"
          >
            <div class="tooltip-step-dot" :style="dotStyle(step.status)"></div>
            <div class="tooltip-step-line">
              <div
                class="tooltip-step-name"
                :class="{ 'muted': step.status === 'pending' }"
                :style="stepNameStyle(step.status)"
              >
                {{ stepIcon(step.status) }} {{ step.label }}
              </div>
              <div class="tooltip-step-time" v-if="step.executed_at">
                {{ formatTime(step.executed_at) }}
              </div>
              <div class="tooltip-step-time" v-else-if="isActive(step.status)">
                Aguardando...
              </div>
              <div class="tooltip-step-time" v-else-if="step.status === 'failed' && step.error_message">
                {{ step.error_message }}
              </div>
              <div class="tooltip-step-time" v-else-if="step.status === 'skipped'">
                Pulado (manual)
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="journey-col" v-else>
    <div class="journey-empty">Sem jornada</div>
  </div>
</template>

<script>
var PREVIEW_COUNT = 3

export default {
  name: 'JourneyProgressCell',
  props: {
    progress: {
      type: Object,
      default: null
    }
  },
  computed: {
    hasError () {
      if (!this.progress) return false
      return this.progress.steps.some(function (s) { return s.status === 'failed' })
    },
    isCancelled () {
      if (!this.progress) return false
      return this.progress.enrollment_status === 'exited' || this.progress.enrollment_status === 'paused'
    },
    isCollapsible () {
      if (!this.progress) return false
      return this.progress.steps.length > PREVIEW_COUNT
    },
    previewSteps () {
      if (!this.progress) return []
      var steps = this.progress.steps

      // Find the current active step (processing/waiting)
      var focusIdx = -1
      for (var i = 0; i < steps.length; i++) {
        if (steps[i].status === 'processing' || steps[i].status === 'waiting') {
          focusIdx = i
          break
        }
      }

      // If no active, find the first error
      if (focusIdx < 0) {
        for (var j = 0; j < steps.length; j++) {
          if (steps[j].status === 'failed') {
            focusIdx = j
            break
          }
        }
      }

      // If no active/error, find the first pending (next to do)
      if (focusIdx < 0) {
        for (var k = 0; k < steps.length; k++) {
          if (steps[k].status === 'pending') {
            focusIdx = k
            break
          }
        }
      }

      // If everything completed/skipped, show last 3
      if (focusIdx < 0) {
        return steps.slice(Math.max(0, steps.length - PREVIEW_COUNT))
      }

      // Anchor focus at the LAST position of preview: [before2, before1, focus]
      var start = Math.max(0, focusIdx - (PREVIEW_COUNT - 1))
      return steps.slice(start, start + PREVIEW_COUNT)
    }
  },
  methods: {
    dotClass (status) {
      var map = {
        completed: 'done',
        processing: 'active',
        waiting: 'active',
        failed: 'error',
        skipped: 'skipped',
        pending: 'pending'
      }
      return map[status] || 'pending'
    },
    connectorClass (currentStatus, nextStatus) {
      // Connector is "done" only if current step is done/skipped
      if (currentStatus === 'completed' || currentStatus === 'skipped') return 'done'
      return 'pending'
    },
    isStepDone (status) {
      return status === 'completed' || status === 'skipped'
    },
    isNextDone (idx) {
      if (!this.progress || idx >= this.progress.steps.length - 1) return false
      var next = this.progress.steps[idx + 1]
      return next.status === 'completed' || next.status === 'skipped' || next.status === 'processing' || next.status === 'waiting'
    },
    isActive (status) {
      return status === 'processing' || status === 'waiting'
    },
    dotStyle (status) {
      var colors = {
        completed: 'background: #22c55e',
        processing: 'background: #3b82f6',
        waiting: 'background: #3b82f6',
        failed: 'background: #ef4444',
        skipped: 'background: #f59e0b',
        pending: 'background: #d4d6de'
      }
      return colors[status] || 'background: #d4d6de'
    },
    stepNameStyle (status) {
      if (status === 'failed') return 'color: #ef4444'
      if (status === 'skipped') return 'color: #f59e0b'
      return ''
    },
    stepIcon (status) {
      var icons = {
        completed: '✓',
        processing: '●',
        waiting: '●',
        failed: '✕',
        skipped: '⊘',
        pending: '○'
      }
      return icons[status] || '○'
    },
    formatTime (isoString) {
      if (!isoString) return ''
      var d = new Date(isoString)
      var day = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
      var time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      return day + ' às ' + time
    }
  }
}
</script>

<style scoped>
/* ================================= */
/* JOURNEY COLUMN COMPONENT          */
/* ================================= */
.journey-col {
  display: flex;
  align-items: center;
  padding: 4px 0;
}

.tooltip-wrapper { position: relative; }

.journey-dots {
  display: flex;
  align-items: center;
  padding: 5px 8px;
  background: #f8f9fb;
  border: 1px solid #eef0f4;
  border-radius: 5px;
  cursor: default;
  transition: all 0.2s ease;
}

.journey-dots:hover { background: #f1f3f6; border-color: #e5e7ed; }

/* ---- Dots ---- */
.j-dot {
  width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.j-dot.done { background: #22c55e; box-shadow: 0 0 0 2px #dcfce7; }
.j-dot.active {
  background: #3b82f6; width: 11px; height: 11px;
  box-shadow: 0 0 0 3px #dbeafe;
  animation: pulse-dot 2.5s ease-in-out infinite;
}
.j-dot.pending { background: #d4d6de; }
.j-dot.error { background: #ef4444; box-shadow: 0 0 0 2px #fee2e2; }
.j-dot.skipped { background: #f59e0b; box-shadow: 0 0 0 2px #fef3c7; }

@keyframes pulse-dot {
  0%, 100% { box-shadow: 0 0 0 3px #dbeafe; }
  50% { box-shadow: 0 0 0 5px rgba(59, 130, 246, 0.15); }
}

/* ---- Connectors ---- */
.j-connector { width: 10px; height: 2px; flex-shrink: 0; border-radius: 1px; }
.j-connector.done { background: #86efac; }
.j-connector.pending { background: #e0e2e9; }

/* ================================= */
/* COLLAPSE / EXPAND LAYERS          */
/* ================================= */
.j-layer {
  display: flex;
  align-items: center;
}

/* Preview visible, full hidden */
.is-collapsible .j-preview { display: flex; }
.is-collapsible .j-full { display: none; }

/* Hover: swap */
.is-collapsible:hover .j-preview { display: none; }
.is-collapsible:hover .j-full { display: flex; }

/* Badge */
.j-badge {
  font-size: 9px;
  font-weight: 700;
  color: #8b8fa3;
  background: #eef0f4;
  border-radius: 4px;
  padding: 1px 5px;
  margin-left: 6px;
  line-height: 1.3;
  white-space: nowrap;
  flex-shrink: 0;
}

.journey-empty { font-size: 11px; color: #b0b4c3; font-style: italic; padding: 4px 0; }

/* ================================= */
/* TOOLTIP                           */
/* ================================= */
.tooltip-content {
  display: none;
  position: absolute;
  top: calc(100% + 12px);
  left: 0;
  background: #ffffff;
  border: 1px solid #e5e7ed;
  border-radius: 14px;
  padding: 16px 18px;
  width: 280px;
  z-index: 1060;
  box-shadow: 0 12px 40px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.06);
}

.tooltip-wrapper:hover .tooltip-content {
  display: block;
  animation: tooltip-in 0.2s ease-out;
}

@keyframes tooltip-in {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}

.tooltip-content::before {
  content: '';
  position: absolute; bottom: 100%; left: 24px;
  border: 7px solid transparent; border-bottom-color: #ffffff;
  filter: drop-shadow(0 -2px 2px rgba(0,0,0,0.04));
}

.tooltip-title {
  font-size: 13px; font-weight: 700; margin-bottom: 14px;
  color: #1a1d2b; display: flex; align-items: center; gap: 6px;
}

.tooltip-title::before {
  content: ''; width: 4px; height: 14px;
  background: #3b82f6; border-radius: 2px; flex-shrink: 0;
}

.tooltip-steps { display: flex; flex-direction: column; }

.tooltip-step {
  display: flex; align-items: flex-start; gap: 10px;
  font-size: 12px; padding: 7px 0; position: relative;
}

.tooltip-step:not(:last-child)::after {
  content: '';
  position: absolute; left: 4.5px; top: 22px; bottom: -2px;
  width: 1.5px; background: #eef0f4;
}

.tooltip-step.done-line:not(:last-child)::after { background: #86efac; }

.tooltip-step-dot {
  width: 10px; height: 10px; border-radius: 50%;
  flex-shrink: 0; margin-top: 1px; position: relative; z-index: 1;
}

.tooltip-step-line { display: flex; flex-direction: column; gap: 2px; flex: 1; }
.tooltip-step-name { color: #1a1d2b; font-weight: 500; font-size: 12px; line-height: 1.3; }
.tooltip-step-name.muted { color: #9498ab; }
.tooltip-step-time { color: #9498ab; font-size: 10px; font-family: 'JetBrains Mono', monospace; }
</style>
